import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../utils/axios";
import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import { Combobox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, ExclamationCircleIcon } from "@heroicons/react/20/solid";
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import Button from "./Button";
import useDialogStore from "../stores/useDialogStore";
import { useQueryClient } from "@tanstack/react-query";

const schema = Joi.object({
    // Accepts either:
    //   - digits only (e.g. "9")   → new client, "A" will be prepended on submit
    //   - "A" + digits (e.g. "A1990") → existing client selected from combobox
    client_id: Joi.string().pattern(/^A?\d+$/).required().messages({
        "string.base": "Client ID must be a string.",
        "string.pattern.base": "Client ID must be a number or start with A followed by numbers.",
        "any.required": "Client ID is required.",
    }),
    client_name: Joi.string().when("client_id", {
        is: Joi.exist(),
        then: Joi.optional(),
        otherwise: Joi.required().messages({
            "string.base": "Client name must be a string.",
            "any.required": "Client name is required when client ID is not provided.",
        }),
    }),
    unique_ids: Joi.string().required().messages({
        "string.base": "Unique IDs must be a string.",
        "any.required": "Unique IDs are required.",
    }),
    reserved_date: Joi.date().required().messages({
        "date.base": "Reserved date must be a valid date.",
        "any.required": "Reserved date is required.",
    }),
});

export default function CreateInvoiceByDeviceId() {
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: joiResolver(schema),
        defaultValues: {
            reserved_date: new Date().toISOString().split("T")[0],
        },
    });

    const { closeDialog } = useDialogStore();
    const queryClient = useQueryClient();

    const [query, setQuery] = useState("");
    const [clients, setClients] = useState([]);
    const [showLoading, setShowLoading] = useState(false);
    const [showNewClientFields, setShowNewClientFields] = useState(false);
    const [isExistingClient, setIsExistingClient] = useState(false);
    const [prices, setPrices] = useState("");
    const [deviceIdsValue, setDeviceIdsValue] = useState("");
    const { data: invoiceNumberData, isLoading: invoiceNumberLoading } = useQuery({
        queryKey: ["next-invoice-number"],
        queryFn: async () => {
            const res = await axiosInstance.get(
                `${window.REACT_APP_API_ENDPOINT}/abaninja/invoices/next-invoice-number`
            );
            return res.data;
        },
        staleTime: 0, // always fetch fresh when modal opens
    });

    const invoiceNumber = invoiceNumberData?.invoice_number ?? null;

    // ── Client ID input — numbers only ──────────────────────────────────────────
    const handleClientIdChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, "");
        setValue("client_id", value.toString());
    };

    // ── Fetch clients on search query change ─────────────────────────────────────
    useEffect(() => {
        let timeoutId;
        setShowLoading(true);

        const fetchClients = async () => {
            try {
                const response = await axiosInstance.post(
                    `${window.REACT_APP_API_ENDPOINT}/users/fetch-users`,
                    query.trim() !== "" ? { q: query } : {}
                );
                setClients(response.data.data);
            } catch (error) {
                console.error("Error fetching clients:", error);
            } finally {
                setShowLoading(false);
            }
        };

        timeoutId = setTimeout(fetchClients, 1500);
        return () => clearTimeout(timeoutId);
    }, [query]);

    // ── Mutation ─────────────────────────────────────────────────────────────────
    const mutation = useMutation({
        mutationFn: async (formData) => {
            // Single call: reserve devices + create AbaNinja invoice.
            // prices is sent as a parallel array to unique_ids so the backend
            // can persist each device's price and build invoice positions correctly.
            const res = await axiosInstance.post(
                `${window.REACT_APP_API_ENDPOINT}/device-statuses/multiple-reserve-devices-with-abaninja`,
                formData
            );
            return res;
        },
        onSuccess: () => {
            toast.success(`Invoice ${invoiceNumber} created successfully!`);
            queryClient.invalidateQueries({ queryKey: ["not-reserved-devices"] });
            queryClient.invalidateQueries({ queryKey: ["reserved-devices"] });
            queryClient.invalidateQueries({ queryKey: ["sold-devices"] });
            queryClient.invalidateQueries({ queryKey: ["invoiceDetails"] });
            closeDialog();
        },
        onError: (error) => {
            toast.error(error.response?.data?.error || "Failed to create invoice.");
        },
    });

    // ── Derived: parse lines for validation + preview ────────────────────────────
    const onSubmit = (data) => {
        const uniqueIds = data.unique_ids
            .split("\n")
            .map((id) => id.trim())
            .filter(Boolean);

        const priceLines = prices
            .split("\n")
            .map((p) => p.trim())
            .filter(Boolean);

        if (uniqueIds.length === 0) {
            toast.error("Please enter at least one device ID.");
            return;
        }

        if (priceLines.length > 0 && priceLines.length !== uniqueIds.length) {
            toast.error(
                `Device IDs (${uniqueIds.length}) and prices (${priceLines.length}) must have the same number of lines.`
            );
            return;
        }

        // Build prices array parallel to unique_ids — null for any unprovided entry
        const pricesArray = uniqueIds.map((_, i) =>
            priceLines[i] ? parseFloat(priceLines[i]) : null
        );

        mutation.mutate({
            // Existing clients already have their full unique_id (e.g. "A1990").
            // New clients are entered as digits only, so prepend "A" here.
            client_id: isExistingClient ? data.client_id : `A${data.client_id}`,
            client_name: data.client_name,
            invoice_number: invoiceNumber,
            unique_ids: uniqueIds,
            prices: pricesArray,
            reserved_date: data.reserved_date,
        });
    };

    const priceLines = prices.split("\n").map((p) => p.trim()).filter(Boolean);

    return (
        <form
            className="max-w-lg mx-auto bg-white p-6 rounded-lg space-y-4"
            onSubmit={handleSubmit(onSubmit)}
        >
            {/* ── Auto-generated invoice number ── */}
            <div className="bg-gray-50 rounded-lg px-4 py-3 flex items-center justify-between border border-gray-200">
                <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                    Invoice Number (auto-generated)
                </span>
                {invoiceNumberLoading ? (
                    <span className="flex items-center gap-1.5 text-gray-400 text-sm">
                        <span className="h-3.5 w-3.5 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin" />
                        Generating...
                    </span>
                ) : (
                    <span className="font-mono text-sm font-semibold text-indigo-700">
                        {invoiceNumber}
                    </span>
                )}
            </div>

            {/* ── Client combobox ── */}
            <div>
                <label className="block font-medium">
                    Client <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                    <Combobox
                        as="div"
                        onChange={(client) => {
                            // Use client.unique_id (e.g. "A1990"), not client.id (DB primary key)
                            // so the backend can match against AbaNinja's customer_number
                            setValue("client_id", client.unique_id);
                            setValue("client_name", client.name);
                            setIsExistingClient(true);
                        }}
                        className="w-full"
                    >
                        <div className="relative">
                            <Combobox.Input
                                className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                onChange={(event) => setQuery(event.target.value)}
                                displayValue={(client) => client?.name || ""}
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </Combobox.Button>

                            {showLoading && query.trim() !== "" && (
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    <Combobox.Option
                                        key="loading"
                                        className="relative cursor-default select-none py-2 pl-3 pr-9 bg-indigo-600 text-white"
                                    >
                                        <span className="block truncate">Loading...</span>
                                    </Combobox.Option>
                                </Combobox.Options>
                            )}

                            {clients.length > 0 && !showLoading && (
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {clients.map((client) => (
                                        <Combobox.Option
                                            key={client.id}
                                            value={client}
                                            className={({ active }) =>
                                                `relative cursor-default select-none py-2 pl-3 pr-9 ${active ? "bg-indigo-600 text-white" : "text-gray-900"
                                                }`
                                            }
                                        >
                                            {({ active, selected }) => (
                                                <>
                                                    <span className={`block truncate ${selected ? "font-semibold" : ""}`}>
                                                        {client.name}
                                                    </span>
                                                    {selected && (
                                                        <span
                                                            className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? "text-white" : "text-indigo-600"
                                                                }`}
                                                        >
                                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </Combobox.Option>
                                    ))}
                                </Combobox.Options>
                            )}

                            {clients.length === 0 && query.trim() !== "" && !showLoading && (
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    <Combobox.Option
                                        key="no-user"
                                        className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900"
                                        disabled
                                    >
                                        <span className="block truncate">No user found</span>
                                    </Combobox.Option>
                                </Combobox.Options>
                            )}
                        </div>
                    </Combobox>

                    {/* Toggle new client fields */}
                    <button
                        className={`${showNewClientFields ? "bg-red-500" : "bg-green-500"
                            } text-white w-9 h-9 rounded-md flex items-center justify-center`}
                        type="button"
                        onClick={() => {
                            setShowNewClientFields(!showNewClientFields);
                            setIsExistingClient(false);
                        }}
                    >
                        {!showNewClientFields ? (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* New client fields */}
                {showNewClientFields && (
                    <div className="mt-2 space-y-2">
                        <div>
                            <label className="block font-medium">
                                New Client Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                {...register("client_name")}
                                className="w-full border border-gray-300 px-3 py-2 rounded-md"
                            />
                            {errors.client_name && (
                                <p className="text-red-500 text-sm">{errors.client_name.message}</p>
                            )}
                        </div>
                        <div>
                            <label className="block font-medium">
                                New Client ID <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <span className="text-gray-500">A</span>
                                </div>
                                <input
                                    type="text"
                                    {...register("client_id")}
                                    onChange={handleClientIdChange}
                                    className="w-full border border-gray-300 pl-8 px-3 py-2 rounded-md"
                                    placeholder="Enter numbers"
                                />
                            </div>
                            {errors.client_id && (
                                <p className="text-red-500 text-sm">{errors.client_id.message}</p>
                            )}
                            <p className="mt-1 text-sm text-gray-500">
                                Numbers only (A will be added automatically)
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Reserved date ── */}
            <div>
                <label className="block font-medium">Reserved Date</label>
                <input
                    type="date"
                    {...register("reserved_date")}
                    className="w-full border border-gray-300 px-3 py-1.5 rounded-md"
                />
                {errors.reserved_date && (
                    <p className="text-red-500 text-sm">{errors.reserved_date.message}</p>
                )}
            </div>

            {/* ── Device IDs + Prices — side by side ── */}
            <div>
                <div className="flex gap-3 mb-1">
                    <label className="flex-1 block font-medium">
                        Unique IDs (one per line) <span className="text-red-500">*</span>
                    </label>
                    <label className="flex-1 block font-medium text-gray-600">
                        Prices{" "}
                        <span className="font-normal text-gray-400 text-sm">(one per line)</span>
                    </label>
                </div>

                <div className="flex gap-3">
                    {/* Device IDs */}
                    <div className="flex-1 relative">
                        <textarea
                            {...register("unique_ids")}
                            rows={6}
                            placeholder={"00001\n00002\n00003"}
                            value={deviceIdsValue}
                            onChange={(e) => {
                                setDeviceIdsValue(e.target.value);
                                setValue("unique_ids", e.target.value);
                            }}
                            className="w-full border border-gray-300 px-3 py-2 rounded-md font-mono resize-none"
                        />
                        {errors.unique_ids && (
                            <p className="text-red-500 text-sm">{errors.unique_ids.message}</p>
                        )}
                    </div>

                    {/* Prices */}
                    <div className="flex-1 relative">
                        <textarea
                            rows={6}
                            placeholder={"150.00\n200.00\n175.00"}
                            value={prices}
                            onChange={(e) => setPrices(e.target.value)}
                            className={`w-full border px-3 py-2 rounded-md font-mono resize-none ${priceLines.length > 0 ? "border-gray-300" : "border-gray-200 bg-gray-50"
                                }`}
                        />
                    </div>
                </div>

                {/* Line mismatch warning — shown only when prices are filled */}
                {(() => {
                    const deviceLineCount = deviceIdsValue.split("\n").map(l => l.trim()).filter(Boolean).length;
                    const mismatch = priceLines.length > 0 && priceLines.length !== deviceLineCount && deviceLineCount > 0;
                    return mismatch ? (
                        <div className="mt-1 flex items-start gap-1.5 text-red-600 text-sm">
                            <ExclamationCircleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                            <span>
                                Prices ({priceLines.length}) must match device IDs ({deviceLineCount}).
                            </span>
                        </div>
                    ) : null;
                })()}
            </div>

            <Button
                type="submit"
                className="bg-bluePrimary text-white px-4 py-2 rounded-md"
                isLoading={mutation.isPending}
                disabled={mutation.isPending || invoiceNumberLoading || !invoiceNumber}
            >
                {mutation.isPending ? "Creating..." : "Create Invoice"}
            </Button>
        </form>
    );
}